import React from "react";

// WeatherBOT card (24 Oct 2026), top-left over the avatar. Data from MBTS
// GET /weather through /api/weather:
//   { city, temp, unit ("F" for US places, else "C"), icon, description,
//     rainChance (0-100 or null), aqi: { us (US AQI, EPA), label, color,
//     index (OpenWeather 1-5) } | null }
// Air quality shows as "● 35 AQI" (the US AQI); the category is in the tooltip.
// Without particle readings it falls back to OpenWeather's label.
// Rain chance and air quality are left out when the forecast has none.
// °C/°F switch (26 Oct 2026): `unit` is the person's choice (remembered by the
// parent); without one, the place's unit (°F in the US, °C elsewhere).
export default function WeatherCard({ weather, compact = false, unit: chosen = null, onUnitChange = null }) {
  if (!weather || !weather.condition) return null;
  const summary = String(weather.description || "").split(".")[0];
  const unit = chosen === "C" || chosen === "F" ? chosen : (weather.unit || "C");
  const byUnit = unit === "F" ? weather.tempF : weather.tempC;
  const temp = Number.isFinite(Number(byUnit)) ? Number(byUnit) : (Number.isFinite(Number(weather.temp)) ? Number(weather.temp) : weather.tempC);
  const range = unit === "F" ? weather.rangeF : weather.rangeC;
  const tooltip = Array.isArray(range) ? `${summary}. ${weather.rangeLabel || "Today"} ${range[0]}–${range[1]}°${unit}.` : (weather.description || "");
  const aqi = weather.aqi && weather.aqi.label ? weather.aqi : null;
  const rain = Number.isFinite(Number(weather.rainChance)) && weather.rainChance !== null ? Number(weather.rainChance) : null;
  const line = { display: "flex", alignItems: "center", gap: "6px" };
  return (
    <div
      role="group"
      aria-label={`Weather${weather.city ? ` in ${weather.city}` : ""}`}
      title={tooltip}
      style={{
        position: "absolute", top: compact ? "8px" : "12px", left: compact ? "8px" : "12px", zIndex: 5,
        minWidth: compact ? "132px" : "164px", padding: compact ? "8px 10px" : "10px 14px",
        borderRadius: "14px", background: "rgba(17, 12, 34, 0.55)", color: "#fff",
        backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
        boxShadow: "0 4px 18px rgba(0,0,0,0.18)", fontSize: compact ? "11px" : "12px", lineHeight: 1.35,
        pointerEvents: "auto",
      }}
    >
      {weather.city && (
        <div style={{ ...line, fontWeight: 600, opacity: 0.9, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: compact ? "150px" : "200px" }}>
          <span aria-hidden="true">📍</span><span data-testid="weather-city">{weather.city}</span>
        </div>
      )}
      <div style={{ ...line, margin: "2px 0" }}>
        <span aria-hidden="true" style={{ fontSize: compact ? "22px" : "28px", lineHeight: 1 }}>{weather.icon}</span>
        <span data-testid="weather-temp" style={{ fontSize: compact ? "22px" : "28px", fontWeight: 700, lineHeight: 1 }}>{temp}°{unit}</span>
        {onUnitChange && (
          <button type="button" data-testid="weather-unit" onClick={() => onUnitChange(unit === "F" ? "C" : "F")}
            aria-label={`Show in °${unit === "F" ? "C" : "F"}`} title={`Show in °${unit === "F" ? "C" : "F"}`}
            style={{ marginLeft: "4px", padding: "2px 6px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.35)", background: "transparent", color: "#fff", fontSize: "11px", cursor: "pointer" }}>
            °{unit === "F" ? "C" : "F"}
          </button>
        )}
      </div>
      {summary && <div style={{ opacity: 0.85, marginBottom: "4px" }}>{summary}</div>}
      <div style={{ ...line, gap: "12px", flexWrap: "wrap" }}>
        {rain !== null && (
          <span data-testid="weather-rain" style={line} title="Chance of rain today"><span aria-hidden="true">💧</span>{rain}%</span>
        )}
        {aqi && (
          <span data-testid="weather-aqi" style={line}
            title={Number.isFinite(aqi.us) ? `US AQI ${aqi.us}: ${aqi.label} (EPA, from PM2.5 and PM10)` : `Air quality ${aqi.index} of 5: ${aqi.label} (OpenWeather)`}>
            <span aria-hidden="true" style={{ width: "9px", height: "9px", borderRadius: "50%", background: aqi.color, display: "inline-block", boxShadow: "0 0 0 2px rgba(255,255,255,0.25)" }} />
            {Number.isFinite(aqi.us) ? `${aqi.us} AQI` : `AQI ${aqi.label}`}
          </span>
        )}
      </div>
    </div>
  );
}
